<template>
  <v-date-picker
    v-model="localValue"
    mode="date"
    :is-dark="isDark"
    :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
    :masks="{ input: inputMask }"
    :popover="disabled ? { visibility: 'hidden' } : { visibility: 'click' }"
    class="shared-date-picker">
    <template #default="{ inputValue, inputEvents }">
      <div class="relative">
        <input type="text" :value="inputValue" v-on="inputEvents" :placeholder="displayPlaceholder" :disabled="disabled"
          :class="[
            error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700',
            inputClass,
          ]"
          class="w-full pr-9 bg-white dark:bg-[#0d1117] border text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow disabled:opacity-50 disabled:cursor-not-allowed" />
        <button v-if="modelValue && !disabled" type="button" title="Clear date" @click.stop="clear"
          class="absolute right-7 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 cursor-pointer">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
        <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
        </svg>
      </div>
    </template>
  </v-date-picker>
</template>

<script setup>
// Thin Vue wrapper around v-calendar's <v-date-picker> — v-model is a plain
// 'yyyy-mm-dd' string (or '') to stay consistent with how every other date
// field in this app is stored/sent, so callers don't need to know v-calendar
// is involved at all. model-config binds that string directly (no manual
// Date <-> ISO conversion needed), independent of masks.input below which
// only controls what's shown/typed in the text field.
import { computed, onMounted } from 'vue'
import { useGlobalStore } from 'stores/global.js'
import { useFormatting } from 'composables/useFormatting.js'

const props = defineProps({
  modelValue: { type: String, default: '' },
  // Unset by default so a format hint derived from the user's own
  // date_format preference (e.g. 'DD/MM/YYYY') is shown instead — pass an
  // explicit placeholder to override that.
  placeholder: { type: String, default: '' },
  error: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  // Includes sizing + radius so callers can match their surrounding form.
  // AttachDependencyForm's regular inputs and InformationSection's compact
  // version-manager mini-form each pass their own.
  inputClass: { type: String, default: 'px-3.5 py-2.5 text-sm rounded-lg' },
})

const emit = defineEmits(['update:modelValue'])

const isDark = document.documentElement.classList.contains('dark')

const globalStore = useGlobalStore()
const { dateFormatMask } = useFormatting()

// user.id (from window.CURRENT_USER) is already set on every authenticated
// page load, so fetchCurrentUser()'s own guard would always short-circuit
// and skip the network call — it can't be relied on to bring in
// date_format. Only refreshUser() actually returns it, and only pages like
// Calendar/Settings call that today, so DatePicker calls it itself here
// (guarded on date_format specifically, not user.id) to make sure the
// preference is loaded wherever a date field shows up.
onMounted(() => {
  if (!globalStore.user.date_format) globalStore.refreshUser()
})

const userDateFormat = computed(() => globalStore.user.date_format || '31/12/2024')
const inputMask = computed(() => dateFormatMask(userDateFormat.value))
// Placeholder shows the token pattern (e.g. 'DD/MM/YYYY') rather than the
// literal sample stored on the user, so it reads as a format hint.
const displayPlaceholder = computed(() => props.placeholder || inputMask.value)

const localValue = computed({
  get: () => props.modelValue || null,
  set: (value) => emit('update:modelValue', value || ''),
})

function clear() {
  emit('update:modelValue', '')
}
</script>

<style scoped>
/* Restyle v-calendar's popover to match our design system (same pattern as
   CalendarSearchDateFilter.vue and availability_calendar.css). */
:deep(.shared-date-picker .vc-popover-content-wrapper) {
  z-index: 60;
}
:deep(.shared-date-picker .vc-title) {
  font-size: 13px;
  font-weight: 600;
}
:deep(.shared-date-picker .vc-weekday) {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
}
:deep(.shared-date-picker .vc-day-content) {
  font-size: 12px;
}
:deep(.shared-date-picker .vc-day-content:hover) {
  background-color: #eff6ff;
  color: #2563eb;
}
:deep(.shared-date-picker .vc-highlight) {
  background-color: #2563eb;
}
:deep(.shared-date-picker .vc-day.is-today .vc-day-content:not(.vc-highlight-content-solid)) {
  font-weight: 700;
  color: #2563eb;
}
:deep(.shared-date-picker.vc-is-dark .vc-title) {
  color: #e5e7eb;
}
:deep(.shared-date-picker.vc-is-dark .vc-weekday) {
  color: #6b7280;
}
:deep(.shared-date-picker.vc-is-dark .vc-day-content:hover) {
  background-color: #1e3a5f;
  color: #60a5fa;
}
</style>
